import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Users, X } from 'lucide-react';
interface FloatingJoinButtonProps {
  label?: string;
}
interface ContactNumber {
  display: string;
  international: string;
}
const CONTACT_NUMBERS: ContactNumber[] = [{
  display: '054-8800260',
  international: '972548800260'
}, {
  display: '052-2632784',
  international: '972522632784'
}, {
  display: '053-3343172',
  international: '972533343172'
}];
const WHATSAPP_MESSAGE = encodeURIComponent('היי, אשמח לשמוע פרטים על הפעילויות בביץ׳ קלאב מעגן');
export function FloatingJoinButton({
  label = 'הצטרפו אלינו'
}: FloatingJoinButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  useEffect(() => {
    // Show button after scrolling down a bit
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setIsMinimized(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (!isDialogOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsDialogOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDialogOpen]);
  return <>
      <AnimatePresence data-id="element-201" data-mp="2xq4bwmxlzpp9">
        {isVisible && <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed bottom-6 left-6 z-40" data-id="element-202" data-mp="2xsgqcq45rhxk">
            {!isMinimized ? <motion.button onClick={() => setIsDialogOpen(true)} className="group relative bg-[#0A5F8F] hover:bg-[#0D7AB8] text-white px-6 py-4 rounded-full shadow-2xl transition-colors duration-200 flex items-center gap-3 font-bold text-lg" whileTap={{
          scale: 0.97
        }} data-id="element-203" data-mp="2e1l583tyy7ew">
                <Users className="w-6 h-6" data-id="element-204" data-mp="2j28vgnadik82" />
                <span data-id="element-205" data-mp="1vbmxxpxzwicb">{label}</span>

                {/* Minimize button */}
                <button onClick={e => {
            e.stopPropagation();
            setIsMinimized(true);
          }} aria-label="הסתירו את הכפתור" className="absolute -top-2 -right-2 bg-white text-[#0A5F8F] rounded-full p-1 shadow-lg hover:bg-gray-100 transition-colors" data-id="element-206" data-mp="1v9ikohe7i6zd">
                  <X className="w-4 h-4" data-id="element-207" data-mp="1bmckyjpmwbhw" />
                </button>
              </motion.button> : <motion.button onClick={() => setIsMinimized(false)} aria-label={label} className="bg-[#0A5F8F] hover:bg-[#0D7AB8] text-white p-4 rounded-full shadow-2xl transition-colors duration-200" whileTap={{
          scale: 0.95
        }} data-id="element-208" data-mp="17k7u1sqklsyy">
                <Users className="w-6 h-6" data-id="element-209" data-mp="nicxqzr63n2u" />
              </motion.button>}
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-210" data-mp="1g8lld74h2zot">
        {isDialogOpen && <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4" dir="rtl" data-id="element-211" data-mp="1g6seprtv0fgt">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onClick={() => setIsDialogOpen(false)} className="absolute inset-0 bg-slate-900/60" aria-hidden="true" data-id="element-212" data-mp="16bgsypk1a7zk" />
            <motion.div role="dialog" aria-modal="true" aria-labelledby="join-dialog-title" initial={{
          opacity: 0,
          scale: 0.96,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 12
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8" data-id="element-213" data-mp="3vxmxw2bhyi7z">
              <button onClick={() => setIsDialogOpen(false)} aria-label="סגירה" className="absolute top-4 left-4 text-gray-400 hover:text-gray-700 transition-colors" data-id="element-214" data-mp="yyja5y6orlp6">
                <X className="w-5 h-5" data-id="element-215" data-mp="k3lfuzrz8m5g" />
              </button>

              <h2 id="join-dialog-title" className="text-2xl font-bold text-gray-900 mb-2" data-id="element-216" data-mp="k338p83s0nir">
                שלחו לנו וואטסאפ
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-217" data-mp="34s96e6mnloyh">
                בחרו מספר ונחזור אליכם עם כל הפרטים על הפעילויות והזמינות.
              </p>

              <ul className="space-y-3" data-id="element-218" data-mp="1pacwfzehxewe">
                {CONTACT_NUMBERS.map(contact => <li key={contact.international} data-id="element-219" data-mp="1u9zauc62916s">
                    <a href={`https://wa.me/${contact.international}?text=${WHATSAPP_MESSAGE}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold py-4 px-5 rounded-xl transition-colors" data-id="element-220" data-mp="1u84qayypbb38">
                      <span className="flex items-center gap-3" data-id="element-221" data-mp="2d1i9y7wyrv3j">
                        <MessageCircle className="w-5 h-5" data-id="element-222" data-mp="2hztkylyzwbnc" />
                        שליחת וואטסאפ
                      </span>
                      <span dir="ltr" className="text-lg tracking-wide" data-id="element-223" data-mp="inhe7vwpno7o">
                        {contact.display}
                      </span>
                    </a>
                  </li>)}
              </ul>

              <p className="text-center text-sm text-gray-500 mt-5" data-id="element-224" data-mp="fmnp2w343df7">
                אפשר גם להתקשר לאותם המספרים בשעות הפעילות.
              </p>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}